import React, { useState, useEffect } from "react";
import setting from '../../config.json';
import './TauriHistoricalTableVisualV2.css';

const TauriHistoricalTableVisualV2 = ({ tableData, tableOptions, columnSize, rowSize, palettes, themes, chartPalettes, onLoadData, paginationProps }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [loading, setLoading] = useState(false);
  const [columnWidths, setColumnWidths] = useState({});

  const handleMouseDown = (key, e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = columnWidths[key] || 150; // Default width

    const handleMouseMove = (moveEvent) => {
      const newWidth = Math.max(startWidth + moveEvent.clientX - startX, 50);
      setColumnWidths((prev) => ({ ...prev, [key]: newWidth }));
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };


  const [tableConfig, settableConfig] = useState({
    binding: {
      data: {
        columns: [
          { name: "Product Name", columnName: "ItemName" }
        ]
      },
      context: {
        point: {
          onClick: {
            selectedValueColumn: "id"
          }
        }
      }
    }
  });

  useEffect(() => {
    const fetchOnLoadData = async () => {
      try {
        const months = [
          "Jan", "Feb", "Mar", "Apr", "May", "Jun",
          "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ];

        const updatedColumns = [
          { name: "Product Name", columnName: "ItemName" }
        ];

        months.forEach(month => {
          updatedColumns.push({
            group: {
              name: month,
              columns: [
                { name: "Unit", columnName: `${month}UNIT`, align: "right" },
                { name: "Peso", columnName: `${month}PESO`, align: "right" }
              ]
            }
          });
        });

        updatedColumns.push({ name: "Total Unit", columnName: "TotalUnit", align: "right" });
        updatedColumns.push({ name: "Total Peso", columnName: "TotalPeso", align: "right" });

        settableConfig(prev => ({
          ...prev,
          binding: {
            ...prev.binding,
            data: {
              ...prev.binding.data,
              columns: updatedColumns
            }
          }
        }));
      } catch (error) {
        console.error(error);
      }
    };

    setLoading(true);
    fetchOnLoadData().finally(() => setLoading(false));
  }, [tableData]);


  const handleSort = (columnName) => {
    let direction = "asc";
    if (sortConfig.key === columnName && sortConfig.direction === "asc") {
      direction = "desc";
    }

    tableData.sort((a, b) => {
      if (a[columnName] < b[columnName]) return direction === "asc" ? -1 : 1;
      if (a[columnName] > b[columnName]) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setSortConfig({ key: columnName, direction });
  };

  const handleMergeSort = (subColumnName) => {
    handleSort(subColumnName);
  };

  const formatNumber = (number, percent) => {
    let formatData = number;
    if (typeof formatData === "number") {
      formatData = formatData.toLocaleString(undefined, { maximumFractionDigits: setting.DISPLAYED_DECIMALS });
      if (percent) formatData += "%";
    }
    return formatData;
  };

  const handleRowClick = (row) => {
    // expand row click logic here
  };

  return (
    <div>
      <div className='chart-content'>
        <div className="tauri-historical-panel-v2">
          <table border="1" className="historical-table">
            <thead>
              <tr>
                {tableConfig.binding.data.columns.map((col, index) => {
                  const isSortable = col.sortable !== false;
                  if (col.group) {
                    return (
                      <th key={index} colSpan={col.group.columns.length} style={{ textAlign: "center", width: columnWidths[index] || "auto" }}>
                        {col.group.name}
                        <div className="resize-handle" onMouseDown={(e) => handleMouseDown(index, e)}></div>
                      </th>
                    );
                  }
                  return (
                    <th
                      key={index}
                      rowSpan={2}
                      style={{
                        cursor: isSortable ? "pointer" : "default",
                        textAlign: col.headerAlign || "center",
                        width: columnWidths[index] || "auto",
                        position: "relative",
                      }}
                      onClick={isSortable ? () => handleSort(col.columnName) : undefined}
                    >
                      {col.name}
                      {sortConfig.key === col.columnName ? (sortConfig.direction === "asc" ? " ▲" : " ▼") : null}
                      <div className="resize-handle" onMouseDown={(e) => handleMouseDown(index, e)}></div>
                    </th>
                  );
                })}
              </tr>
              <tr>
                {tableConfig.binding.data.columns.map((col, index) => {
                  if (col.group) {
                    return col.group.columns.map((subCol, subIndex) => (
                      <th
                        key={`${index}-${subIndex}`}
                        style={{
                          cursor: "pointer",
                          textAlign: "center",
                          width: columnWidths[`${index}-${subIndex}`] || "auto",
                          position: "relative",
                        }}
                        onClick={() => handleMergeSort(subCol.columnName)}
                      >
                        {subCol.name}
                        <div className="resize-handle" onMouseDown={(e) => handleMouseDown(`${index}-${subIndex}`, e)}></div>
                        {sortConfig.key === subCol.columnName ? (sortConfig.direction === "asc" ? " ▲" : " ▼") : null}
                      </th>
                    ));
                  }
                  return null;
                })}
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, index) => (
                <tr key={index} onClick={() => handleRowClick(row)} style={{ '--table-hierarchy-hover-color': chartPalettes[0].ranges[2] }}>
                  {tableConfig.binding.data.columns.map((col, idx) => {
                    if (col.group) {
                      return col.group.columns.map((subCol, subIndex) => (
                        <td key={`${idx}-${subIndex}`} style={{ textAlign: subCol.align || "left", width: columnWidths[idx] || "auto" }}>
                          {formatNumber(row[subCol.columnName], subCol.percent)}
                        </td>
                      ));
                    }
                    return (
                      <td key={idx} style={{ textAlign: col.align || "left", width: columnWidths[idx] || "auto" }}>
                        {formatNumber(row[col.columnName], col.percent)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TauriHistoricalTableVisualV2;
